import React from "react";
import { Button, Modal, Skeleton } from "../../../components/ui";
import { useExercisePlan } from "../hooks/useExercisePlan";
import { formatDuration } from "../exerciseMapping";

export interface ViewExerciseModalProps {
  /** The plan's `public_id`, which is what the detail call is made with. */
  planId: string;
  /** How many exercises the row said were on it — see `rows` below. */
  exerciseCount: number;
  onClose: () => void;
}

/** One line: what is done, and for how long. */
const ExerciseLine: React.FC<{ type: string; minutes: number }> = ({
  type,
  minutes,
}) => (
  <div className="flex items-baseline justify-between gap-4 border-t border-line py-4">
    <dt className="min-w-0 break-words text-[14px] 3xl:text-[15px] text-ink">
      {type}
    </dt>
    <dd className="shrink-0 text-[14px] 3xl:text-[15px] text-ink">
      {formatDuration(minutes)}
    </dd>
  </div>
);

/**
 * An exercise plan read rather than edited: what is done, and for how long.
 *
 * The list row carries a count and a total but no exercises — the detail
 * endpoint is the only place those come from — so this dialog is what makes
 * that call. It opens straight away rather than waiting for it: a dialog that
 * appears only once a request has landed reads as a press that did nothing.
 *
 * A list and not a table, though it has two columns. There are only ever a
 * handful of lines and nothing to sort or scan across — the headings above
 * them say which side is which, and a table's machinery would be borrowed for
 * a pair of figures that fit on one line each.
 */
const ViewExerciseModal: React.FC<ViewExerciseModalProps> = ({
  planId,
  exerciseCount,
  onClose,
}) => {
  const { plan, status, error, reload } = useExercisePlan(planId);

  // As many blocks as the row said there were exercises, so the dialog is the
  // height it will keep and the lines do not shuffle down as they land. A row
  // claiming none still draws one, since a dialog of nothing is not a shape.
  const rows = Math.min(Math.max(exerciseCount, 1), 8);

  return (
    <Modal
      open
      title="View exercise"
      onClose={onClose}
      className="max-w-[620px]"
      scrollBody
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose}>
            Close
          </Button>
          {status === "failed" && (
            <Button size="md" onClick={reload}>
              Try again
            </Button>
          )}
        </>
      }
    >
      <dl className="flex flex-col">
        <div className="flex items-baseline justify-between gap-4 pb-2 text-[13px] text-ink-muted">
          <span>Exercise type</span>
          <span>Duration</span>
        </div>

        {status === "loading" && (
          <>
            {/* The shimmer is the shape of an answer rather than the answer,
                so it is hidden from screen readers and this line stands for
                it. */}
            <span role="status" className="sr-only">
              Loading this plan’s exercises…
            </span>
            {Array.from({ length: rows }, (_, index) => (
              <div key={index} className="border-t border-line py-4">
                <Skeleton className="h-[18px] w-[45%] rounded" />
              </div>
            ))}
          </>
        )}

        {status === "failed" && (
          <p className="border-t border-line py-4 text-[14px] text-ink-muted">
            {error ?? "We could not load this plan’s exercises."}
          </p>
        )}

        {status === "ready" &&
          (plan && plan.entries.length ? (
            plan.entries.map((entry) => (
              <ExerciseLine
                key={entry.id}
                type={entry.type || "Unnamed exercise"}
                minutes={entry.minutes}
              />
            ))
          ) : (
            // A plan is only written with lines on it, so this is the shape of
            // a record that has been emptied elsewhere rather than a state the
            // dialog is opened in — said plainly instead of drawing nothing.
            <p className="border-t border-line py-4 text-[14px] text-ink-muted">
              Nothing is on this plan.
            </p>
          ))}
      </dl>
    </Modal>
  );
};

export default ViewExerciseModal;
